import { useTranslation } from 'react-i18next';
import type { CoatColorLocus } from './coatColor';

export type CoatTypeTrait = 'Furnishings' | 'Length' | 'Texture' | 'Shedding';

export const COAT_TYPE_TRAITS: CoatTypeTrait[] = ['Furnishings', 'Length', 'Texture', 'Shedding'];

/** Aus den Felltyp-Genen abgeleitetes Fell (GET /dogs/:id/health-tests/coat-type). Null = Gen nicht getestet. */
export interface CoatType {
  /** z.B. { locus: "RSPO2", genotype: "F/IC" } */
  loci: CoatColorLocus[];
  /** "Furnished" oder "Unfurnished" */
  furnishings: string | null;
  /** "Short" oder "Long" */
  length: string | null;
  /** "Straight", "Wavy" oder "Curly" */
  texture: string | null;
  /** "Seasonal" oder "Light" */
  shedding: string | null;
  /** Verdeckte Anlagen: "ImproperCoat", "LongHair", "Curl" */
  carries: string[];
}

/** Erwartete Verteilung eines Merkmals unter den Welpen. */
export interface CoatTypeTraitOutcome {
  trait: CoatTypeTrait;
  values: { value: string; percent: number }[];
}

const traitValue = (coatType: CoatType, trait: CoatTypeTrait) =>
  ({ Furnishings: coatType.furnishings, Length: coatType.length, Texture: coatType.texture, Shedding: coatType.shedding })[trait];

export const useCoatTypeText = () => {
  const { t } = useTranslation();

  const trait = (code: CoatTypeTrait) => t(`health:coatType.traits.${code}`);

  const value = (code: CoatTypeTrait, valueCode: string) => t(`health:coatType.values.${code}.${valueCode}`);

  /** "Mit Bart & Augenbrauen · Langes Fell · Gewellt · Wenig Haarwechsel" - null ohne auswertbares Merkmal. */
  const label = (coatType: CoatType) => {
    const parts = COAT_TYPE_TRAITS.flatMap((code) => {
      const valueCode = traitValue(coatType, code);
      return valueCode ? [value(code, valueCode)] : [];
    });
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  const carries = (codes: string[]) => codes.map((code) => t(`health:coatType.carries.${code}`)).join(', ');

  return { trait, value, label, carries };
};
